import React, { useState } from 'react';
import { X, UserPlus, CheckCircle, Loader2 } from 'lucide-react';
import ImageUploadV2 from '../common/ImageUploadV2';

const QuickAddParticipantModal = ({ event, onClose, onSuccess }) => {
    const [formData, setFormData] = useState(() => {
        const initial = {};
        event.participantFields?.forEach(field => {
            initial[field.fieldName] = '';
        });
        return initial;
    });
    const [selectedTicketId, setSelectedTicketId] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const activeTickets = event.tickets?.filter(t => t.isActive) || [];
    const categories = event.participantCategories || [];

    const setField = (fieldName, value) =>
        setFormData(prev => ({ ...prev, [fieldName]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        const missing = event.participantFields
            ?.filter(f => f.validation === 'required' && !formData[f.fieldName])
            .map(f => f.fieldName);
        if (missing?.length > 0) {
            alert(`Please fill in: ${missing.join(', ')}`);
            return;
        }
        if (event.eventType === 'paid' && !selectedTicketId) {
            alert('Please select a ticket');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                data: formData,
                ...(selectedTicketId && { ticketId: selectedTicketId }),
                ...(selectedCategoryId && { categoryId: selectedCategoryId }),
            };

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${event._id}/participants`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                }
            );
            const result = await res.json();

            if (result.success) {
                setSubmitted(true);
                setTimeout(() => {
                    onSuccess?.();
                    onClose();
                }, 1800);
            } else {
                alert('Failed: ' + (result.message || 'Unknown error'));
            }
        } catch {
            alert('Failed to add participant. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddAnother = () => {
        const initial = {};
        event.participantFields?.forEach(f => { initial[f.fieldName] = ''; });
        setFormData(initial);
        setSelectedTicketId('');
        setSelectedCategoryId('');
        setSubmitted(false);
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-green-100 rounded-lg">
                            <UserPlus className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-900">Quick Add Participant</h2>
                            <p className="text-xs text-gray-500">{event.eventName}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-6 py-5">
                    {submitted ? (
                        <div className="flex flex-col items-center justify-center py-14 text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle className="w-9 h-9 text-green-500" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">Participant Added!</h3>
                            <p className="text-sm text-gray-500 mb-6">Successfully registered. Refreshing list…</p>
                            <button
                                onClick={handleAddAnother}
                                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                            >
                                + Add Another
                            </button>
                        </div>
                    ) : (
                        <form id="quick-add-form" onSubmit={handleSubmit} className="space-y-4">

                            {/* Category selector — free events with categories */}
                            {event.eventType === 'free' && categories.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category
                                    </label>
                                    <select
                                        value={selectedCategoryId}
                                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">No category</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Ticket selector — paid events */}
                            {event.eventType === 'paid' && activeTickets.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ticket <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={selectedTicketId}
                                        onChange={(e) => setSelectedTicketId(e.target.value)}
                                        required
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">Select a ticket…</option>
                                        {activeTickets.map(ticket => (
                                            <option key={ticket.id} value={ticket.id}>
                                                {ticket.name}
                                                {ticket.price ? ` — ₹${ticket.discountPrice && ticket.discountPrice < ticket.price ? ticket.discountPrice : ticket.price}` : ' — Free'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Divider when there's a ticket/category section */}
                            {((event.eventType === 'free' && categories.length > 0) ||
                              (event.eventType === 'paid' && activeTickets.length > 0)) && (
                                <hr className="border-gray-100" />
                            )}

                            {/* Participant fields */}
                            {event.participantFields?.map(field => (
                                <div key={field.id}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {field.fieldName}
                                        {field.validation === 'required' && (
                                            <span className="text-red-500 ml-1">*</span>
                                        )}
                                    </label>

                                    {field.fieldType === 'file' ? (
                                        <ImageUploadV2
                                            onUploadSuccess={(url) => setField(field.fieldName, url)}
                                            onUploadError={(err) => console.error('Upload error:', err)}
                                            uploadEndpoint="/api/v2/events/upload/participant-profile"
                                            currentImageUrl={formData[field.fieldName]}
                                            label=""
                                        />
                                    ) : field.fieldType === 'textarea' ? (
                                        <textarea
                                            value={formData[field.fieldName] || ''}
                                            onChange={(e) => setField(field.fieldName, e.target.value)}
                                            placeholder={field.placeholder}
                                            required={field.validation === 'required'}
                                            rows={3}
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                                        />
                                    ) : field.fieldType === 'select' ? (
                                        <select
                                            value={formData[field.fieldName] || ''}
                                            onChange={(e) => setField(field.fieldName, e.target.value)}
                                            required={field.validation === 'required'}
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="">Select {field.fieldName}</option>
                                            {field.options?.map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type={field.fieldType}
                                            value={formData[field.fieldName] || ''}
                                            onChange={(e) => setField(field.fieldName, e.target.value)}
                                            placeholder={field.placeholder}
                                            required={field.validation === 'required'}
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    )}

                                    {field.helpText && (
                                        <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                                    )}
                                </div>
                            ))}
                        </form>
                    )}
                </div>

                {/* Footer */}
                {!submitted && (
                    <div className="px-6 py-4 border-t shrink-0 flex items-center justify-end gap-3 bg-gray-50 rounded-b-2xl">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            form="quick-add-form"
                            type="submit"
                            disabled={submitting}
                            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Adding…
                                </>
                            ) : (
                                <>
                                    <UserPlus className="w-4 h-4" />
                                    Add Participant
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuickAddParticipantModal;
